import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AuthSliceModel from "./authSliceModel";
import { AuthResponseModel, UserModel } from "../../../domain/models/userModel";
import axios from "axios";
import { GraphQLClient } from "../../../../../shared/gql-client";
import { ApolloError, gql } from "@apollo/client/core";
import * as gqlBuilder from "gql-query-builder";

const saveSessionStorage = (state: UserModel) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("session", serializedState);
  } catch (err) {
    console.error(err);
  }
};

export const resetSessionStorage = () => {
  try {
    localStorage.removeItem("session");
    localStorage.removeItem("csrf_token");
    localStorage.removeItem("token");
    window.location.reload();
  } catch (err) {
    console.error(err);
  }
};

const loadSessionStorage = () => {
  try {
    const serializedState = localStorage.getItem("session");
    if (serializedState === null) {
      return {};
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return {};
  }
};

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };

    try {
      // const storage = loadSessionStorage();
      // if (storage) {
      //   return storage;
      // }

      // resetSessionStorage();

      let currentUser: UserModel = {
        id: 1,
        name: "",
        username: "",
        email: "",
        token: "",
        role: "",
        availableRoles: [],
      };

      const response = await client.query(
        "currentUser",
        gql(`
      query Current_user {
        currentUser {
          id
          email
          name
          status
          roles {
                name
          }
          availableRoles
        }
      }
      `),
      );

      if (response) {
        currentUser = response as UserModel;
      }

      // if (response.data !== undefined && response.data.data !== undefined) {
      //   currentUser.id = response.data.data.drupal_internal__uid;
      //   currentUser.name = response.data.data.display_name;
      //   currentUser.email = response.data.data?.active_relation?.meta?.drupal_internal__target_id ?? null;
      //   currentUser.token = response.data.data?.active_relation?.id ?? null;
      // }

      // saveSessionStorage(currentUser);

      return currentUser;
    } catch (error: unknown) {
      console.log(error);
      return thunkAPI.rejectWithValue("Something went wrong, please try again");
    }
  },
);

export const logout = createAsyncThunk("auth/logout", async (_, thunkAPI) => {
  try {
    const { client } = thunkAPI.extra as { client: GraphQLClient };

    await client.mutate(
      "logout",
      gql(`
            mutation Logout {
              logout {
                  id
              }
            }
          `),
      {},
    );
    resetSessionStorage();
    return { success: true };
  } catch (error: unknown) {
    console.log(error);

    return thunkAPI.rejectWithValue(error);
  }
});

const persistedState = loadSessionStorage();

const initialState = {
  loading: false,
  error: null,
  user: persistedState as UserModel,
  csrfLoading: false,
  csrfLoaded: false,
} as AuthSliceModel;

export const getAuthCSRF = createAsyncThunk(
  "auth/getAuthCSRF",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/sanctum/csrf-cookie");

      if (response.status > 199 && response.status < 300) {
        return "Loaded";
      }

      return thunkAPI.rejectWithValue("Something went wrong");
    } catch (e) {
      return thunkAPI.rejectWithValue("Something went wrong");
    }
  },
);

export const attemptLogin = createAsyncThunk(
  "auth/attemptLogin",
  async (props: { userString: string; password: string }, thunkAPI) => {
    const { userString, password } = props;
    const { client } = thunkAPI.extra as { client: GraphQLClient };

    const { query, variables } = gqlBuilder.mutation({
      operation: "login",
      variables: {
        userString: { value: userString, required: true },
        password: { value: password, required: true },
      },
      fields: [
        "token",
        {
          user: [
            "id",
            "name",
            "username",
            "email",
            {
              roles: ["name"],
            },
            "availableRoles",
          ],
        },
      ],
    });

    try {
      const response: AuthResponseModel = await client.mutate(
        "login",
        gql(query),
        variables,
      );

      const user = {
        id: response.user.id,
        name: response.user.name,
        username: response.user.username,
        email: response.user.email,
        token: response.token ?? "",
        role: response.user.roles[0].name,
        availableRoles: response.user.availableRoles,
      };

      localStorage.setItem("token", response.token ?? "");

      saveSessionStorage(user);

      await thunkAPI.dispatch(getCurrentUser());

      return user;
    } catch (e) {
      if (e instanceof ApolloError) {
        return thunkAPI.rejectWithValue(e.message);
      }

      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const sendForgotPasswordMail = createAsyncThunk(
  "auth/sendForgotPasswordMail",
  async (props: { userString: string; newAccount?: boolean }, thunkAPI) => {
    const { userString, newAccount } = props;
    const { client } = thunkAPI.extra as { client: GraphQLClient };

    try {
      return await client.mutate(
        "forgotPassword",
        gql(`
            mutation forgotPassword {
              forgotPassword(userString: "${userString}", ${newAccount ? "newAccount: true" : ""}) {
                status
                message
              }
            }
          `),
      );
    } catch (e) {
      return thunkAPI.rejectWithValue("Something went wrong");
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (props: { token: string; password: string }, thunkAPI) => {
    const { token, password } = props;
    const { client } = thunkAPI.extra as { client: GraphQLClient };

    try {
      return await client.mutate(
        "resetPassword",
        gql(`
              mutation resetPassword {
                resetPassword(token: "${token}", password: "${password}") {
                  status
                  message
                }
              }
            `),
      );
    } catch (e) {
      return thunkAPI.rejectWithValue("Something went wrong");
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAuthCSRF.pending, (state) => {
      state.csrfLoading = true;
    });
    builder.addCase(getAuthCSRF.rejected, (state, action) => {
      state.csrfLoading = false;
      state.error = action.payload as string;
      state.csrfLoaded = true;
    });
    builder.addCase(getAuthCSRF.fulfilled, (state) => {
      state.csrfLoading = false;
      state.csrfLoaded = true;
    });
    builder.addCase(attemptLogin.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(attemptLogin.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(attemptLogin.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload as UserModel;
    });
    builder.addCase(getCurrentUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCurrentUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload as UserModel;
    });
    builder.addCase(logout.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.loading = false;
      state.user = {} as UserModel;
    });
  },
});

export default authSlice.reducer;
